import * as React from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

interface stepperProps {
  steps: Array<string>;
  onFinish: Function;
  id?: string;
  allowReset?: boolean;
  onReset?: Function;
  optionalSteps?: Array<boolean>;
  buttonTextBack?: string;
  buttonTextNext?: string;
  buttonTextFinish?: string;
  buttonTextSkip?: string;
  buttonTextReset?: string;
  children?: any;
  sx?: any;
}

const FaStepper = (props: stepperProps) => {

  const {
    steps,
    optionalSteps,
    id,
    onFinish,
    buttonTextBack,
    buttonTextNext,
    buttonTextFinish,
    buttonTextSkip,
    buttonTextReset,
    children,
    sx
  } = props;

  const fallbackTransl = {
    back: 'Back',
    next: 'Next',
    finish: 'Finish',
    skip: 'Skip',
    reset: 'Reset',
  };

  const allowReset = props.allowReset ?? false;
  const onReset = props.onReset ?? function () {
  };

  const boxSx = {
    ...{
      width: '100%'
    },
    ...sx
  };

  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set<number>());

  const isStepOptional = (step: number) => {
    return optionalSteps && optionalSteps[step];
  };

  const isStepSkipped = (step: number) => {
    return skipped.has(step);
  };

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      if (allowReset)
        onFinish();
      else
        return onFinish();
    }

    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSkip = () => {
    if (!isStepOptional(activeStep)) {
      // You probably want to guard against something like this,
      // it should never occur unless someone's actively trying to break something.
      throw new Error("You can't skip a step that isn’t optional.");
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);
      return newSkipped;
    });
  };

  const handleReset = () => {
    onReset();
    setActiveStep(0);
  };

  return (
      <Box sx={boxSx}>
        <Stepper id={id ?? ''} activeStep={activeStep}>
          {steps.map((label, index) => {
            const stepProps: { completed?: boolean } = {};
            const labelProps: { optional?: React.ReactNode; } = {};
            if (isStepOptional(index)) {
              labelProps.optional = (
                  <Typography variant="caption">Optional</Typography>
              );
            }
            if (isStepSkipped(index)) {
              stepProps.completed = false;
            }
            return (
                <Step key={label} {...stepProps}>
                  <StepLabel {...labelProps}>{label}</StepLabel>
                </Step>
            );
          })}
        </Stepper>
        {activeStep === steps.length ? (
            allowReset &&
            <React.Fragment>
              { children.filter((child:any) => child.props.step === "complete") }
              <Box sx={{display: 'flex', flexDirection: 'row', pt: 2}}>
                <Box sx={{flex: '1 1 auto'}}/>
                <Button variant="contained" onClick={handleReset}>
                  {buttonTextReset ?? fallbackTransl.reset}
                </Button>
              </Box>
            </React.Fragment>
        ) : (
            <React.Fragment>
              <>
                { children.filter((child:any) => child.props.step === activeStep) }
                <Box sx={{display: 'flex', flexDirection: 'row', pt: 2}}>
                  <Button
                      variant="outlined"
                      disabled={activeStep === 0}
                      onClick={handleBack}
                      sx={{mr: 1}}
                  >
                    {buttonTextBack ?? fallbackTransl.back}
                  </Button>
                  <Box sx={{flex: '1 1 auto'}}/>
                  {isStepOptional(activeStep) && (
                      <Button variant="outlined" onClick={handleSkip} sx={{mr: 1}}>
                        {buttonTextSkip ?? fallbackTransl.skip}
                      </Button>
                  )}
                  <Button variant="contained" onClick={handleNext}>
                    {activeStep === steps.length - 1
                        ? (buttonTextFinish ?? fallbackTransl.finish)
                        : (buttonTextNext ?? fallbackTransl.next)}
                  </Button>
                </Box>
              </>
            </React.Fragment>
        )}
      </Box>
  );
}

interface stepProps {
  step: number|string;
  children?: any;
  sx?: any;
}

const FaStep = (props: stepProps) => {

  const boxSx = {
    ...{width: '100%', mt: 2, mb: 1},
    ...props.sx
  }

  return (
      <Box id={`step-${props.step}`} sx={boxSx}>{props.children}</Box>
  )
};

export {FaStepper, FaStep};
